import { useState, useEffect, useMemo } from 'react';
import { fetchApi } from '../lib/api';

const SCORE_COLOR = (score) => {
  if (score >= 75) return { bg: '#DCFCE7', text: '#166534', label: 'Strong' };
  if (score >= 55) return { bg: '#FEF3C7', text: '#92400E', label: 'Good' };
  if (score >= 35) return { bg: '#FEE2E2', text: '#991B1B', label: 'Weak' };
  return { bg: '#F1F5F9', text: '#475569', label: 'Poor' };
};

export default function DemoInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('grid'); // grid | trends | toyetic
  const [editingPrinciplesFor, setEditingPrinciplesFor] = useState(null);
  const [principlesDraft, setPrinciplesDraft] = useState('');
  const [savingPrinciples, setSavingPrinciples] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [generateCount, setGenerateCount] = useState(5);
  const [selectedIdeaId, setSelectedIdeaId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchApi('/api/admin/demo-insights');
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const matchesByIdea = useMemo(() => {
    if (!data) return {};
    const out = {};
    data.matches.forEach(m => {
      if (!out[m.idea_id]) out[m.idea_id] = {};
      out[m.idea_id][m.company_id] = m;
    });
    return out;
  }, [data]);

  const trendMatchesByIdea = useMemo(() => {
    if (!data) return {};
    const out = {};
    data.trend_matches.forEach(m => {
      if (!out[m.idea_id]) out[m.idea_id] = [];
      out[m.idea_id].push(m);
    });
    return out;
  }, [data]);

  const trendsById = useMemo(() => {
    if (!data) return {};
    const out = {};
    data.trends.forEach(t => { out[t.id] = t; });
    return out;
  }, [data]);

  const inventorsById = useMemo(() => {
    if (!data) return {};
    const out = {};
    data.inventors.forEach(i => { out[i.id] = i; });
    return out;
  }, [data]);

  const handleEditPrinciples = (org) => {
    setEditingPrinciplesFor(org.id);
    setPrinciplesDraft(org.toyetic_principles || '');
  };

  const handleSavePrinciples = async (orgId) => {
    setSavingPrinciples(true);
    try {
      await fetchApi(`/api/admin/companies/${orgId}/toyetic-principles`, {
        method: 'PUT',
        body: JSON.stringify({ toyetic_principles: principlesDraft }),
      });
      setEditingPrinciplesFor(null);
      await load();
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSavingPrinciples(false);
    }
  };

  const handleGenerate = async (orgId) => {
    setGeneratingFor(orgId);
    try {
      await fetchApi(`/api/admin/companies/${orgId}/generate-tailored-ideas`, {
        method: 'POST',
        body: JSON.stringify({ count: generateCount }),
      });
      await load();
    } catch (err) {
      alert('Generation failed: ' + err.message);
    } finally {
      setGeneratingFor(null);
    }
  };

  if (loading) return <div className="p-6 text-vault-slate">Loading demo insights…</div>;
  if (error) return <div className="p-6 text-vault-error">Error: {error}</div>;
  if (!data) return null;

  const ideas = data.ideas;
  const companies = data.companies;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-heading font-bold text-vault-navy">Demo Insights</h1>
        <p className="text-sm text-vault-slate mt-1">
          God-mode view: {ideas.length} inventor concept{ideas.length === 1 ? '' : 's'} × {companies.length} compan{companies.length === 1 ? 'y' : 'ies'} with wishlists, trend matches, and toyetic principles.
        </p>
      </header>

      <div className="flex gap-2 border-b border-vault-border">
        {[
          { key: 'grid', label: 'Match Grid' },
          { key: 'trends', label: 'Trend Alignment' },
          { key: 'toyetic', label: 'Toyetic Principles & Tailored Ideas' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-vault-gold text-vault-navy'
                : 'border-transparent text-vault-slate hover:text-vault-navy'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'grid' && (
        <MatchGrid
          ideas={ideas}
          companies={companies}
          matchesByIdea={matchesByIdea}
          inventorsById={inventorsById}
          onSelectIdea={setSelectedIdeaId}
          selectedIdeaId={selectedIdeaId}
        />
      )}

      {activeTab === 'trends' && (
        <TrendsView
          ideas={ideas}
          trendMatchesByIdea={trendMatchesByIdea}
          trendsById={trendsById}
          trends={data.trends}
        />
      )}

      {activeTab === 'toyetic' && (
        <ToyeticView
          companies={companies}
          editingPrinciplesFor={editingPrinciplesFor}
          principlesDraft={principlesDraft}
          setPrinciplesDraft={setPrinciplesDraft}
          onEdit={handleEditPrinciples}
          onCancel={() => setEditingPrinciplesFor(null)}
          onSave={handleSavePrinciples}
          savingPrinciples={savingPrinciples}
          generatingFor={generatingFor}
          generateCount={generateCount}
          setGenerateCount={setGenerateCount}
          onGenerate={handleGenerate}
        />
      )}

      {selectedIdeaId && (
        <IdeaDetailDrawer
          idea={ideas.find(i => i.id === selectedIdeaId)}
          matches={matchesByIdea[selectedIdeaId] || {}}
          companies={companies}
          trendMatches={trendMatchesByIdea[selectedIdeaId] || []}
          trendsById={trendsById}
          inventor={inventorsById[(ideas.find(i => i.id === selectedIdeaId) || {}).user_id]}
          onClose={() => setSelectedIdeaId(null)}
        />
      )}
    </div>
  );
}

function MatchGrid({ ideas, companies, matchesByIdea, inventorsById, onSelectIdea, selectedIdeaId }) {
  if (!ideas.length || !companies.length) {
    return <div className="text-vault-slate p-8 text-center">No ideas or companies to display yet. Run the seed script.</div>;
  }
  return (
    <div className="bg-white/95 backdrop-blur border border-vault-border rounded-glass shadow-glass overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-vault-surface border-b border-vault-border">
            <tr>
              <th className="text-left p-3 font-medium text-vault-navy" style={{ minWidth: 240 }}>Inventor Concept</th>
              {companies.map(c => (
                <th key={c.id} className="text-center p-3 font-medium text-vault-navy" style={{ minWidth: 140 }}>
                  <div className="leading-tight">{c.org?.company_name || c.company_name}</div>
                  <div className="text-xs font-normal text-vault-slate mt-0.5">
                    {(c.categories_seeking || []).slice(0, 2).join(' · ')}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ideas.map(idea => {
              const inv = inventorsById[idea.user_id];
              const isSelected = selectedIdeaId === idea.id;
              return (
                <tr
                  key={idea.id}
                  className={`border-b border-vault-border cursor-pointer transition ${isSelected ? 'bg-amber-50' : 'hover:bg-vault-surface'}`}
                  onClick={() => onSelectIdea(idea.id)}
                >
                  <td className="p-3 align-top">
                    <div className="font-medium text-vault-navy">{idea.title}</div>
                    <div className="text-xs text-vault-slate mt-0.5">
                      {idea.profile?.product_category || idea.category || '—'} · {idea.profile?.target_age_range || '—'} · {idea.profile?.mechanism_type || '—'}
                    </div>
                    {inv && (
                      <div className="text-xs text-vault-slate mt-0.5 italic">by {inv.display_name}</div>
                    )}
                  </td>
                  {companies.map(c => {
                    const m = matchesByIdea[idea.id]?.[c.id];
                    if (!m) {
                      return (
                        <td key={c.id} className="p-3 text-center align-top">
                          <span className="text-xs text-vault-slate">—</span>
                        </td>
                      );
                    }
                    const sc = SCORE_COLOR(m.score);
                    return (
                      <td key={c.id} className="p-3 text-center align-top">
                        <div
                          className="inline-flex flex-col items-center px-3 py-2 rounded-xl"
                          style={{ backgroundColor: sc.bg, color: sc.text, minWidth: 88 }}
                          title={m.explanation}
                        >
                          <div className="text-2xl font-bold leading-none">{m.score}</div>
                          <div className="text-xs font-medium mt-1">{m.score_label || sc.label}</div>
                          {m.trend_modifier !== 0 && m.trending_topic && (
                            <div className="text-[10px] mt-1 opacity-80">
                              {m.trend_modifier > 0 ? '+' : ''}{m.trend_modifier} via {m.trending_topic.slice(0, 18)}
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="p-3 bg-vault-surface text-xs text-vault-slate border-t border-vault-border">
        Click any row to see the full match explanation, trend alignment, and inventor context for that concept.
      </div>
    </div>
  );
}

function TrendsView({ ideas, trendMatchesByIdea, trendsById, trends }) {
  return (
    <div className="space-y-4">
      <div className="bg-white/95 backdrop-blur border border-vault-border rounded-glass shadow-glass p-5">
        <h3 className="font-heading font-bold text-vault-navy text-lg mb-3">Active Trends ({trends.length})</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {trends.map(t => {
            const dirColor = t.trend_direction === 'rising' ? '#10B981'
              : t.trend_direction === 'peaking' ? '#F59E0B'
              : t.trend_direction === 'declining' ? '#F97066'
              : '#94A3B8';
            return (
              <div key={t.id} className="border border-vault-border rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-vault-navy text-sm">{t.trend_topic}</div>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: dirColor + '22', color: dirColor }}>
                    {t.trend_direction}
                  </span>
                </div>
                <div className="text-xs text-vault-slate mt-1 leading-snug">{t.trend_summary}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white/95 backdrop-blur border border-vault-border rounded-glass shadow-glass p-5">
        <h3 className="font-heading font-bold text-vault-navy text-lg mb-3">Idea × Trend Alignment</h3>
        <div className="space-y-3">
          {ideas.map(idea => {
            const matches = trendMatchesByIdea[idea.id] || [];
            if (!matches.length) return null;
            return (
              <div key={idea.id} className="border border-vault-border rounded-xl p-4">
                <div className="font-medium text-vault-navy">{idea.title}</div>
                <div className="text-xs text-vault-slate mt-0.5 mb-3">
                  {idea.profile?.product_category || '—'} · {idea.profile?.target_age_range || '—'}
                </div>
                <div className="space-y-2">
                  {matches.sort((a, b) => (b.match_score || 0) - (a.match_score || 0)).map((m, i) => {
                    const trend = trendsById[m.trend_id];
                    return (
                      <div key={i} className="bg-vault-surface rounded-lg p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-medium text-vault-navy">{trend?.trend_topic || 'Unknown trend'}</div>
                            <div className="text-xs text-vault-slate mt-1">{m.match_explanation}</div>
                            {m.suggested_pitch_angle && (
                              <div className="text-xs text-vault-navy mt-1 italic">Pitch angle: {m.suggested_pitch_angle}</div>
                            )}
                          </div>
                          <div className="text-sm font-bold text-vault-gold">{Math.round((m.match_score || 0) * 100)}%</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {ideas.every(i => !(trendMatchesByIdea[i.id] || []).length) && (
            <div className="text-vault-slate text-sm">No trend matches yet — run the seed script.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ToyeticView({
  companies, editingPrinciplesFor, principlesDraft, setPrinciplesDraft,
  onEdit, onCancel, onSave, savingPrinciples,
  generatingFor, generateCount, setGenerateCount, onGenerate,
}) {
  return (
    <div className="space-y-5">
      {companies.map(c => {
        const org = c.org;
        const isEditing = editingPrinciplesFor === org.id;
        const isGenerating = generatingFor === org.id;
        const concepts = Array.isArray(org.tailored_concepts) ? org.tailored_concepts : [];
        return (
          <div key={c.id} className="bg-white/95 backdrop-blur border border-vault-border rounded-glass shadow-glass p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="font-heading font-bold text-vault-navy text-xl">{org.company_name}</h3>
                <p className="text-sm text-vault-slate mt-0.5">{org.company_description}</p>
              </div>
              {!isEditing && (
                <button
                  onClick={() => onEdit(org)}
                  className="px-3 py-1.5 text-sm rounded-btn border border-vault-border text-vault-navy hover:bg-vault-surface"
                >
                  {org.toyetic_principles ? 'Edit Principles' : 'Add Principles'}
                </button>
              )}
            </div>

            <div className="border border-vault-border rounded-xl p-4 bg-vault-surface mb-4">
              <div className="text-xs font-medium text-vault-slate uppercase tracking-wide mb-2">Toyetic Principles</div>
              {isEditing ? (
                <div>
                  <textarea
                    value={principlesDraft}
                    onChange={(e) => setPrinciplesDraft(e.target.value)}
                    rows={10}
                    placeholder="Describe what makes a great toy for this company. The system uses these principles to generate tailored idea concepts from inventor submissions."
                    className="w-full p-3 rounded-input border border-vault-border text-sm text-vault-navy bg-white focus:outline-none focus:border-vault-gold"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => onSave(org.id)}
                      disabled={savingPrinciples}
                      className="px-4 py-2 text-sm rounded-btn text-white font-medium disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
                    >
                      {savingPrinciples ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={onCancel}
                      className="px-4 py-2 text-sm rounded-btn border border-vault-border text-vault-navy hover:bg-vault-surface"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : org.toyetic_principles ? (
                <pre className="whitespace-pre-wrap text-sm text-vault-navy font-body leading-relaxed">{org.toyetic_principles}</pre>
              ) : (
                <div className="text-sm italic text-vault-slate">No toyetic principles yet. Add some so the AI can generate tailored ideas for this company.</div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <div className="font-medium text-vault-navy">Tailored Idea Concepts</div>
                <div className="text-xs text-vault-slate">
                  AI cross-references the company's toyetic principles with all inventor submissions to generate net-new concepts.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={generateCount}
                  onChange={(e) => setGenerateCount(Number(e.target.value))}
                  className="px-2 py-1.5 text-sm rounded-input border border-vault-border bg-white"
                  disabled={isGenerating || isEditing}
                >
                  {[3, 5, 7, 10].map(n => <option key={n} value={n}>{n} ideas</option>)}
                </select>
                <button
                  onClick={() => onGenerate(org.id)}
                  disabled={isGenerating || isEditing || !org.toyetic_principles}
                  className="px-4 py-2 text-sm rounded-btn text-white font-medium disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
                >
                  {isGenerating ? 'Generating…' : (concepts.length ? 'Regenerate' : 'Generate Tailored Ideas')}
                </button>
              </div>
            </div>

            {concepts.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-3">
                {concepts.map((concept, i) => (
                  <div key={i} className="border border-vault-border rounded-xl p-4 bg-white">
                    <div className="font-heading font-bold text-vault-navy">{concept.title}</div>
                    {concept.tagline && <div className="text-sm text-vault-gold mt-1 italic">{concept.tagline}</div>}
                    <div className="text-sm text-vault-navy mt-2 leading-snug">{concept.description}</div>
                    {concept.principles_alignment && (
                      <div className="text-xs text-vault-slate mt-2 border-l-2 border-vault-gold pl-2">
                        <strong>Why it fits:</strong> {concept.principles_alignment}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      {concept.target_age && <span className="px-2 py-0.5 bg-vault-surface rounded-full text-vault-slate">Age {concept.target_age}</span>}
                      {concept.estimated_price && <span className="px-2 py-0.5 bg-vault-surface rounded-full text-vault-slate">{concept.estimated_price}</span>}
                    </div>
                    {Array.isArray(concept.inspired_by) && concept.inspired_by.length > 0 && (
                      <div className="text-xs text-vault-slate mt-2">
                        <strong>Built from:</strong> {concept.inspired_by.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-vault-slate italic">
                {org.toyetic_principles
                  ? 'Click "Generate Tailored Ideas" to have the AI synthesize concepts.'
                  : 'Add toyetic principles first, then generate.'}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function IdeaDetailDrawer({ idea, matches, companies, trendMatches, trendsById, inventor, onClose }) {
  if (!idea) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ backgroundColor: 'rgba(15, 23, 42, 0.4)' }} onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-heading font-bold text-2xl text-vault-navy">{idea.title}</h2>
            {inventor && <div className="text-sm text-vault-slate mt-0.5">Submitted by {inventor.display_name}</div>}
          </div>
          <button onClick={onClose} className="text-vault-slate hover:text-vault-navy text-2xl leading-none">×</button>
        </div>

        <div className="text-sm text-vault-navy mb-4 leading-relaxed">{idea.description}</div>

        {idea.profile && (
          <div className="grid grid-cols-4 gap-2 mb-5 text-xs">
            <div className="p-2 bg-vault-surface rounded-lg">
              <div className="text-vault-slate">Category</div>
              <div className="font-medium text-vault-navy">{idea.profile.product_category}</div>
            </div>
            <div className="p-2 bg-vault-surface rounded-lg">
              <div className="text-vault-slate">Age</div>
              <div className="font-medium text-vault-navy">{idea.profile.target_age_range}</div>
            </div>
            <div className="p-2 bg-vault-surface rounded-lg">
              <div className="text-vault-slate">Mechanic</div>
              <div className="font-medium text-vault-navy">{idea.profile.mechanism_type}</div>
            </div>
            <div className="p-2 bg-vault-surface rounded-lg">
              <div className="text-vault-slate">Price tier</div>
              <div className="font-medium text-vault-navy">{idea.profile.estimated_cost_tier}</div>
            </div>
          </div>
        )}

        <h3 className="font-heading font-bold text-lg text-vault-navy mb-2">Company Match Scores</h3>
        <div className="space-y-2 mb-5">
          {companies.map(c => {
            const m = matches[c.id];
            if (!m) return null;
            const sc = SCORE_COLOR(m.score);
            return (
              <div key={c.id} className="border border-vault-border rounded-xl p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-vault-navy">{c.org?.company_name || c.company_name}</div>
                  <div className="px-3 py-1 rounded-full text-sm font-bold" style={{ backgroundColor: sc.bg, color: sc.text }}>
                    {m.score} · {m.score_label || sc.label}
                  </div>
                </div>
                <div className="text-sm text-vault-slate mt-2">{m.explanation}</div>
                {m.trending_topic && (
                  <div className="text-xs text-vault-navy mt-2 italic">
                    {m.trend_modifier > 0 ? '+' : ''}{m.trend_modifier} trend modifier — {m.trending_topic} ({m.trend_reasoning})
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {trendMatches.length > 0 && (
          <>
            <h3 className="font-heading font-bold text-lg text-vault-navy mb-2">Trend Alignment</h3>
            <div className="space-y-2">
              {trendMatches.sort((a, b) => (b.match_score || 0) - (a.match_score || 0)).map((m, i) => {
                const trend = trendsById[m.trend_id];
                return (
                  <div key={i} className="border border-vault-border rounded-xl p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium text-vault-navy">{trend?.trend_topic || 'Unknown'}</div>
                      <div className="text-sm font-bold text-vault-gold">{Math.round((m.match_score || 0) * 100)}%</div>
                    </div>
                    <div className="text-sm text-vault-slate mt-1">{m.match_explanation}</div>
                    {m.suggested_pitch_angle && (
                      <div className="text-sm text-vault-navy mt-1 italic">Pitch angle: {m.suggested_pitch_angle}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
